import React, { useState, useEffect, useRef } from "react";

import {
  Card,
  Grid,
  Button,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";


import { get, getFile, post, delNoResponse, postUpload } from "utils/WebService";
import { errorAlert, successAlert } from "utils/Functions";
import { toDateTime } from "utils/DateUtils";
import SugestoesDocx from "./SugestoesDocx";
import EditorModelo, { htmlDoEditorAntigo, inserirTexto } from "./EditorModelo";
import useVersaoModelos from "./useVersaoModelos";
import { coresDoTema } from "assets/coresTema";

function MalaDiretaModelos() {
  const [modelos, setModelos] = useState([]);
  const [variaveis, setVariaveis] = useState([]);
  // Carteira do modelo: "Universal" (todas as carteiras; qualquer usuário cria e altera) ou uma das carteiras das equipes
  // do usuário (sem equipe com carteira, todas); o de uma carteira só quem é dela altera.
  const [carteirasModelo, setCarteirasModelo] = useState({ carteiras: [], podeUniversal: false });
  const [carteiraModelo, setCarteiraModelo] = useState(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [currentModelo, setCurrentModelo] = useState({ id: null, nome: "", assunto: "", conteudoHtml: "" });
  const [lastFocusedField, setLastFocusedField] = useState("corpo");

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Editor do corpo (Jodit): o texto é lido dele ao salvar; o conteudoInicial só muda ao abrir o modelo ou importar o .docx
  const editorRef = useRef(null);
  const [conteudoInicial, setConteudoInicial] = useState("");
  const arquivoRef = useRef(null);
  // Importação do .docx: trechos que viram variável e avisos (o que não coube no editor)
  const [sugestoes, setSugestoes] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [importando, setImportando] = useState(false);

  // Alteração feita por outro usuário (ou em outra aba): a lista se recarrega sozinha; com o modelo aberto no editor,
  // aparece o aviso (alterado ou excluído) em vez de o "Salvar" substituir a versão do outro sem ninguém perceber
  const modeloAbertoRef = useRef(null);
  const [avisoAlteracao, setAvisoAlteracao] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState("");
  const ACOES = { criou: "criou", alterou: "alterou", excluiu: "excluiu" };
  const { marcarVisto } = useVersaoModelos(async (info) => {
    const lista = await fetchModelos();
    if (info?.quem && info?.nome) {
      setUltimaAtualizacao(`Lista atualizada: ${info.quem} ${ACOES[info.acao] || "alterou"} o modelo "${info.nome}" em ${toDateTime(info.quando)}.`);
    }
    const aberto = modeloAbertoRef.current;
    if (!aberto?.id || !lista) return;
    const porQuem = info?.modeloId === aberto.id && info?.quem ? `por ${info.quem} em ${toDateTime(info.quando)}` : "por outro usuário";
    const atual = lista.find((m) => m.cvId === aberto.id);
    if (!atual) {
      setAvisoAlteracao({ tipo: "excluido", texto: `Este modelo foi excluído ${porQuem}. Ele não pode mais ser salvo: use "Salvar como modelo novo" para não perder o texto.` });
    } else if (atual.impressao !== aberto.impressao) {
      setAvisoAlteracao({ tipo: "alterado", modelo: atual, texto: `Este modelo foi alterado ${porQuem} depois que você o abriu. Se salvar agora, o que está aqui substitui a versão dele.` });
    }
  });

  useEffect(() => {
    fetchModelos();
    fetchVariaveis();
    get("/mala-direta/modelos/carteiras")
      .then((r) => setCarteirasModelo({ carteiras: r?.carteiras || [], podeUniversal: !!r?.podeUniversal }))
      .catch(() => null);
  }, []);

  const UNIVERSAL = { value: "", label: "Universal (todas as carteiras)" };
  const opcoesCarteira = [...(carteirasModelo.podeUniversal ? [UNIVERSAL] : []), ...carteirasModelo.carteiras];
  /** A carteira do modelo na tela: a gravada; no novo, universal (quem pode) ou a única carteira do usuário. */
  const carteiraDoModelo = (idCarteira, novo) => {
    if (idCarteira != null) {
      return carteirasModelo.carteiras.find((c) => Number(c.value) === Number(idCarteira)) || { value: idCarteira, label: `Carteira #${idCarteira}` };
    }
    if (!novo || carteirasModelo.podeUniversal) return UNIVERSAL;
    return carteirasModelo.carteiras.length === 1 ? carteirasModelo.carteiras[0] : null;
  };

  const fetchModelos = async () => {
    try {
      const res = await get("/mala-direta/modelos");
      setModelos(res || []);
      return res || [];
    } catch (err) {
      errorAlert(err?.message || "Erro ao carregar os modelos.");
      return null;
    }
  };

  const fetchVariaveis = async () => {
    try {
      const res = await get("/mala-direta/variaveis");
      setVariaveis(res || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenEditor = (modelo = { id: null, nome: "", assunto: "", conteudoHtml: "" }) => {
    modeloAbertoRef.current = modelo.id ? { id: modelo.id, impressao: modelo.impressao } : null;
    setAvisoAlteracao(null);
    setCurrentModelo(modelo);
    setCarteiraModelo(carteiraDoModelo(modelo.carteira, !modelo.id));
    setConteudoInicial(htmlDoEditorAntigo(modelo.conteudoHtml));
    setLastFocusedField("corpo");
    setSugestoes([]);
    setAvisos([]);
    setIsEditing(true);
  };

  const handleCloseEditor = () => {
    modeloAbertoRef.current = null;
    setAvisoAlteracao(null);
    editorRef.current = null;
    setIsEditing(false);
    setSugestoes([]);
    setAvisos([]);
  };

  // Modelo em .docx (carta do Word): o backend converte para o editor e sugere os trechos que viram variável.
  // Nada é gravado até o "Salvar". Dentro do editor, substitui o texto do modelo aberto (nome e assunto ficam).
  const importarDocx = async (e) => {
    const arquivo = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!arquivo) return;
    const atual = editorRef.current ? editorRef.current.value : currentModelo.conteudoHtml;
    const temTexto = isEditing && (atual || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    if (temTexto && !window.confirm("Substituir o texto deste modelo pelo do .docx?")) return;
    const fd = new FormData();
    fd.append("arquivo", arquivo);
    setImportando(true);
    try {
      const res = await postUpload("/mala-direta/modelos/importar-docx", fd);
      const base = isEditing ? currentModelo : { id: null, nome: "", assunto: "", conteudoHtml: "" };
      if (!isEditing) setCarteiraModelo(carteiraDoModelo(null, true));
      const html = res.html || "";
      setCurrentModelo({ ...base, nome: base.nome || res.nome || "", conteudoHtml: html });
      setConteudoInicial(html);
      // Com o editor já aberto, o texto vai direto para ele (o mesmo .docx importado de novo não muda o conteudoInicial)
      if (isEditing && editorRef.current) editorRef.current.value = html;
      setSugestoes((res.sugestoes || []).map((s, i) => ({ ...s, chave: i, marcada: !!s.variavel })));
      setAvisos(res.avisos || []);
      setIsEditing(true);
    } catch (err) {
      errorAlert(err?.message || "Erro ao importar o .docx.");
    } finally {
      setImportando(false);
    }
  };

  const handleSave = async () => {
    if (!carteiraModelo) {
      errorAlert("Escolha a carteira do modelo.");
      return;
    }
    try {
      const salvo = await post("/mala-direta/modelos", {
        cvId: currentModelo.id,
        cvIdCarteira: carteiraModelo.value === "" ? null : carteiraModelo.value,
        ccNome: currentModelo.nome,
        ccAssunto: currentModelo.assunto,
        ccConteudoHtml: editorRef.current ? editorRef.current.value : currentModelo.conteudoHtml,
      });
      // A alteração desta tela não volta como aviso
      marcarVisto(salvo?.versao);
      setUltimaAtualizacao("");
      fetchModelos();
      handleCloseEditor();
      successAlert("Modelo salvo.");
    } catch (err) {
      errorAlert(err?.message || "Erro ao salvar o modelo.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Deseja realmente excluir este modelo?")) {
      try {
        await delNoResponse(`/mala-direta/modelos/${id}`);
        fetchModelos();
      } catch (err) {
        errorAlert(err?.message || "Erro ao excluir o modelo.");
      }
    }
  };

  const insertVariable = (varId) => {
    if (lastFocusedField === "assunto") {
      setCurrentModelo(prev => ({
        ...prev,
        assunto: (prev.assunto || "") + varId
      }));
    } else {
      // No lugar do cursor (ou do trecho selecionado, com a formatação dele); sem cursor no texto, no fim
      inserirTexto(editorRef.current, varId);
    }
  };

  const entradaDocx = (
    <input
      ref={arquivoRef}
      type="file"
      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      style={{ display: "none" }}
      onChange={importarDocx}
    />
  );

  if (isEditing) {
    return (
      <Grid container display="flex" justifyContent="center">
        {entradaDocx}
        <Grid item xs={12} md={12}>
          <Card>
            <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5">{currentModelo.id ? "Editar Modelo" : "Novo Modelo"}</MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton variant="outlined" color="info" disabled={importando} onClick={() => arquivoRef.current && arquivoRef.current.click()}>
                  {importando ? "Importando..." : "Importar .docx"}
                </MDButton>
                <MDButton color="light" onClick={handleCloseEditor}>
                  Voltar
                </MDButton>
              </MDBox>
            </MDBox>
            <Divider />
            {avisoAlteracao && (
              <MDBox mx={3} mt={2} px={2} py={1.5} display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap"
                sx={{ borderRadius: 1, border: `1px solid ${coresDoTema(darkMode).borda}`, backgroundColor: coresDoTema(darkMode).painel }}>
                <MDTypography variant="button" color="warning" fontWeight="medium">{avisoAlteracao.texto}</MDTypography>
                {avisoAlteracao.tipo === "alterado" ? (
                  <MDButton variant="outlined" color="info" size="small" onClick={() => {
                    if (window.confirm("Descartar o que você mudou aqui e carregar a versão salva?")) {
                      const m = avisoAlteracao.modelo;
                      handleOpenEditor({ id: m.cvId, nome: m.ccNome, assunto: m.ccAssunto, conteudoHtml: m.ccConteudoHtml, carteira: m.cvIdCarteira, impressao: m.impressao });
                    }
                  }}>
                    Carregar a versão salva
                  </MDButton>
                ) : (
                  <MDButton variant="outlined" color="info" size="small" onClick={() => {
                    modeloAbertoRef.current = null;
                    setCurrentModelo((prev) => ({ ...prev, id: null }));
                    setAvisoAlteracao(null);
                  }}>
                    Salvar como modelo novo
                  </MDButton>
                )}
              </MDBox>
            )}
            <MDBox p={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={8}>
                      <MDInput
                        label="Nome do Modelo"
                        fullWidth
                        value={currentModelo.nome}
                        onChange={(e) => setCurrentModelo({ ...currentModelo, nome: e.target.value })}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDBox mt={2}>
                        <MDSelect
                          name="carteiraModelo"
                          options={opcoesCarteira}
                          getOptionLabel={(o) => o.label}
                          getOptionValue={(o) => String(o.value)}
                          value={carteiraModelo}
                          onChange={setCarteiraModelo}
                          placeholder="Carteira do modelo *"
                          isClearable={false}
                        />
                        <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>
                          {carteirasModelo.podeUniversal
                            ? "Universal: vale para todas as carteiras. De uma carteira: só quem vê a carteira usa, e só para as fichas dela."
                            : "O modelo fica na carteira escolhida: só quem vê a carteira usa, e só para as fichas dela."}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>
                  <MDInput
                    label="Assunto do E-mail (Você pode usar variáveis aqui)"
                    fullWidth
                    value={currentModelo.assunto || ""}
                    onChange={(e) => setCurrentModelo({ ...currentModelo, assunto: e.target.value })}
                    onFocus={() => setLastFocusedField("assunto")}
                    margin="normal"
                  />
                  
                  <MDBox mt={2} mb={2}>
                    <MDTypography variant="subtitle2" color="text" gutterBottom>
                      Inserir Variável Dinâmica:
                    </MDTypography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <MDSelect
                          name="variavel"
                          options={variaveis}
                          getOptionLabel={(v) => `${v.nome} (${v.id})`}
                          getOptionValue={(v) => v.id}
                          onChange={(option) => {
                            if (option) insertVariable(option.id);
                          }}
                          placeholder="Selecione a variável para inserir no texto..."
                          isClearable={false}
                          value={null}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="caption" color="text">
                          A variável entra onde você clicou por último (Assunto ou Corpo do E-mail). Com um trecho do texto selecionado, ela troca o trecho, mantendo a formatação.
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </MDBox>
                  <SugestoesDocx
                    sugestoes={sugestoes}
                    setSugestoes={setSugestoes}
                    avisos={avisos}
                    variaveis={variaveis}
                    editorRef={editorRef}
                    darkMode={darkMode}
                  />
                  <MDBox mt={2} mb={1}>
                    <MDTypography variant="subtitle2" color="text">
                      Corpo do E-mail (importe o .docx ou cole do Word)
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      Como no Word: fonte, tamanho, espaçamento, cores, tabela (mesclar células, bordas), imagem (arraste a borda para
                      redimensionar), link e símbolos. A área branca mostra o texto como ele vai no e-mail; o tracejado das tabelas
                      sem borda só aparece aqui.
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ maxWidth: "1200px", margin: "0 auto" }}>
                    <EditorModelo
                      valor={conteudoInicial}
                      darkMode={darkMode}
                      editorRef={editorRef}
                      aoFocar={() => setLastFocusedField("corpo")}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
            <Divider />
            <MDBox p={2} display="flex" justifyContent="flex-end" gap={2}>
              <MDButton onClick={handleCloseEditor} color="light">
                Cancelar
              </MDButton>
              <MDButton onClick={handleSave} color="info" variant="contained">
                Salvar
              </MDButton>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container display="flex" justifyContent="center">
      {entradaDocx}
      <Grid item xs={12} md={12}>
        <Card>
          <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5">Modelos de Mala Direta</MDTypography>
            <MDBox display="flex" gap={1}>
              <MDButton variant="outlined" color="info" disabled={importando} onClick={() => arquivoRef.current && arquivoRef.current.click()}>
                {importando ? "Importando..." : "Importar .docx"}
              </MDButton>
              <MDButton
                variant="contained"
                color="info"
                startIcon={<AddIcon />}
                onClick={() => handleOpenEditor()}
              >
                Novo Modelo
              </MDButton>
            </MDBox>
          </MDBox>
          <Divider />
          <MDBox p={3}>
            {ultimaAtualizacao && (
              <MDTypography variant="caption" color="text" display="block" mb={1}>{ultimaAtualizacao}</MDTypography>
            )}
            <List>
              {modelos.map((mod) => (
                <ListItem key={mod.cvId} divider sx={{ pr: mod.podeAlterar ? 18 : 8 }}>
                  <ListItemText 
                    primary={<MDTypography variant="button" color="text" fontWeight="medium">{mod.ccNome}</MDTypography>} 
                    secondary={<MDTypography variant="caption" color="text">{`${mod.carteira ? `Carteira: ${mod.carteira}` : "Universal"} | Assunto: ${mod.ccAssunto || "Sem assunto"} | Criado em: ${mod.cdCriacao ? toDateTime(mod.cdCriacao) : ""}${mod.criadoPor ? ` por ${mod.criadoPor}` : ""}${mod.podeAlterar ? "" : " | Só quem é da carteira altera"}`}</MDTypography>}
                  />
                  <ListItemSecondaryAction>
                    {/* Baixar em Word (.docx, com as imagens; as variáveis ficam como estão): para quem vê o modelo */}
                    <Tooltip title="Baixar o modelo (Word)">
                      <IconButton edge="end" color="info"
                        onClick={() => getFile(`/mala-direta/modelos/${mod.cvId}/word`, `${(mod.ccNome || "Modelo").replace(/[\\/:*?"<>|]/g, "_")}.docx`)
                          .catch((e) => errorAlert(e?.message || "Erro ao baixar o modelo."))}>
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    {mod.podeAlterar && (
                      <>
                        <IconButton edge="end" color="info" onClick={() => handleOpenEditor({ id: mod.cvId, nome: mod.ccNome, assunto: mod.ccAssunto, conteudoHtml: mod.ccConteudoHtml, carteira: mod.cvIdCarteira, impressao: mod.impressao })}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" color="error" onClick={() => handleDelete(mod.cvId)}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            {modelos.length === 0 && <MDTypography variant="button" color="text" fontWeight="regular">Nenhum modelo cadastrado.</MDTypography>}
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
}

export default MalaDiretaModelos;
